import React, { useState, useMemo, useCallback } from 'react';
import { Space, Button, Input, Select, Tag, Grid, Upload, Tooltip } from 'antd';
import {
  DownloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  SearchOutlined,
  CloseOutlined,
  UpOutlined,
  DownOutlined,
  EditOutlined,
  ClockCircleOutlined,
  FormOutlined,
  RedoOutlined,
  FileTextOutlined,
  UploadOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { InputRef } from 'antd';
import ExcelGuideModal from './ExcelGuideModal';
import { selectUser } from '../../../Modules/User/Presentation/selectors';
import { useAppSelector } from '../../../Modules/Presentation/hooks';
import { isSlotOperationRestricted } from '../../../Utils/timeRestriction';
import { getBatchPermissions } from '../../../Utils/slotPermissions';
import { isAdpa99Username } from '../../../Utils/usernameSuffix';
import { Slot } from '../../../Modules/Slot/Model/Slot';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const { Option } = Select;

interface Props {
  selected: React.Key[];
  selectedSlots: Slot[];
  searchField: 'slot' | 'keyword' | 'url' | 'user' | 'creator' | 'mid_single' | 'mid_compare';
  onSearchFieldChange: (v: Props['searchField']) => void;
  // 정렬
  sortBy: 'start_date' | 'end_date' | 'registered';
  sortDir: 'asc' | 'desc';
  onSortByChange: (v: 'start_date' | 'end_date' | 'registered') => void;
  onSortDirChange: (v: 'asc' | 'desc') => void;
  onAdd: () => void;
  onExtend: () => void;
  onEdit: () => void;
  onRefund: () => void;
  onDelete: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  onSearch: (
    value: string,
    event?: React.ChangeEvent<HTMLInputElement> | React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLInputElement>,
    info?: { source?: 'clear' | 'input' },
  ) => void;
  status: string;
  onStatusChange: (v: string) => void;
  onBatchEdit: () => void;
  onExcelParsedUpload: (file: File) => void;
  onExcelBatchUpdateUpload: (file: File) => void;
  onExcelDownload: () => void;
  onExcelDownloadWithLatestRanks: () => void;
  excelUploading: boolean;
  excelDownloading?: boolean;
  localFindOpen: boolean;
  localFindQuery: string;
  localFindMatchCount: number;
  localFindActiveMatchIndex: number;
  localFindInputRef: React.Ref<InputRef>;
  onOpenLocalFind: () => void;
  onCloseLocalFind: () => void;
  onLocalFindQueryChange: (value: string) => void;
  onLocalFindPrev: () => void;
  onLocalFindNext: () => void;
}

const Bar = styled.div`
  width: 100%;
  padding: 12px 16px;
  border-radius: 0;
  background: transparent;
  border: none;
  display: grid;
  grid-template-rows: auto auto;
  gap: 10px;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
`;

const RightCluster = styled(Space)`
  display: flex;
  flex-shrink: 0;
`;

const SearchArea = styled(Space)`
  flex: 1;
  min-width: 280px;
`;

const SelectedTag = styled(Tag)`
  && {
    background: rgba(176, 48, 82, 0.08);
    border-color: rgba(176, 48, 82, 0.2);
    color: #B03052;
    margin-right: 4px;
  }
`;

const ManagementBar: React.FC<Props> = ({
  selected,
  selectedSlots,
  searchField,
  onSearchFieldChange,
  sortBy,
  sortDir,
  onSortByChange,
  onSortDirChange,
  onAdd,
  onExtend,
  onEdit,
  onRefund,
  onDelete,
  search,
  onSearchChange,
  onSearch,
  status,
  onStatusChange,
  onBatchEdit,
  onExcelDownload,
  onExcelDownloadWithLatestRanks,
  onExcelParsedUpload,
  onExcelBatchUpdateUpload,
  excelUploading,
  excelDownloading = false,
  localFindOpen,
  localFindQuery,
  localFindMatchCount,
  localFindActiveMatchIndex,
  localFindInputRef,
  onOpenLocalFind,
  onCloseLocalFind,
  onLocalFindQueryChange,
  onLocalFindPrev,
  onLocalFindNext,
}) => {
  const hasSelection = selected.length > 0;
  const hasSingle = selected.length === 1;
  const [guideOpen, setGuideOpen] = useState(false);
  const currentUser = useAppSelector(selectUser);
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  
  // 시간 제한 확인
  const isRestricted = currentUser ? isSlotOperationRestricted(currentUser) : false;

  // 선택 슬롯 권한 계산은 선택/사용자 변경 때만 재계산
  const batchPermissions = useMemo(
    () => getBatchPermissions(selectedSlots, currentUser, isRestricted),
    [selectedSlots, currentUser, isRestricted]
  );

  const selectedSlot = useMemo(() => hasSingle ? selectedSlots[0] : null, [hasSingle, selectedSlots]);

  /** Ant Design Search는 조합 입력 중 Enter를 막는데, 명시 핸들러로 조합 종료 뒤 Enter 검색을 보장 */
  const handleSearchPressEnter = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.nativeEvent.isComposing) {
        return;
      }
      onSearch(e.currentTarget.value, e, { source: 'input' });
    },
    [onSearch],
  );

  const prefill = useMemo(() => {
    if (!selectedSlot) return null;
    const rawMid = (selectedSlot.mid_compare || selectedSlot.mid_single || '').toString();
    const digits = rawMid.match(/\d+/)?.[0] ?? '';
    return {
      mid: digits,
      main_keyword: selectedSlot.main_keyword || '',
      product_url: selectedSlot.product_url || '',
    };
  }, [selectedSlot]);

  const hasLocalFindQuery = localFindQuery.trim().length > 0;
  const localFindStatusText = useMemo(() => {
    if (!localFindOpen || !hasLocalFindQuery) return '입력 대기';
    if (localFindMatchCount === 0) return '결과 없음';
    return `${localFindActiveMatchIndex + 1} / ${localFindMatchCount}`;
  }, [hasLocalFindQuery, localFindActiveMatchIndex, localFindMatchCount, localFindOpen]);

  return (
    <Bar>
      <Row>
        <SearchArea size={isMobile ? 'small' : 'middle'} wrap style={{ width: isMobile ? '100%' : undefined }}>
          <Select size={isMobile ? 'small' : 'middle'} value={searchField} onChange={onSearchFieldChange} style={{ width: 140 }}>
            <Option value="slot">슬롯 번호</Option>
            <Option value="user">아이디</Option>
            <Option value="creator">생성자</Option>
            <Option value="keyword">키워드</Option>
            <Option value="url">URL</Option>
            <Option value="mid_single">단일 MID</Option>
            <Option value="mid_compare">가격비교 MID</Option>
          </Select>
          {searchField !== 'mid_single' && searchField !== 'mid_compare' && (
            <Input.Search
              size={isMobile ? 'small' : 'middle'}
              placeholder={searchField === 'url' ? 'URL 입력' : '검색어 입력'}
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              onSearch={onSearch}
              onPressEnter={handleSearchPressEnter}
              allowClear
              enterButton={<span><SearchOutlined /> 검색</span>}
              style={{ width: '100%' }}
            />
          )}
          {(searchField === 'mid_single' || searchField === 'mid_compare') && (
            <Input.Search
              size={isMobile ? 'small' : 'middle'}
              placeholder={`${searchField === 'mid_single' ? '단일' : '가격비교'} MID 입력`}
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              onSearch={onSearch}
              onPressEnter={handleSearchPressEnter}
              allowClear
              enterButton={<span><SearchOutlined /> 검색</span>}
              style={{ width: '100%' }}
            />
          )}
        </SearchArea>
        <RightCluster size={isMobile ? 'small' : 'middle'} wrap style={{ width: isMobile ? '100%' : undefined }}>
          {/* 정렬 영역 */}
          <Space size={isMobile ? 'small' : 'middle'}>
            <Select
              size={isMobile ? 'small' : 'middle'}
              value={sortBy}
              onChange={onSortByChange}
              style={{ width: 140 }}
            >
              <Option value="registered">등록순</Option>
              <Option value="start_date">시작일</Option>
              <Option value="end_date">종료일</Option>
            </Select>
            <Button
              size={isMobile ? 'small' : 'middle'}
              onClick={() => onSortDirChange(sortDir === 'asc' ? 'desc' : 'asc')}
              icon={sortDir === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
            >
              {sortDir === 'asc' ? '오름차순' : '내림차순'}
            </Button>
          </Space>
          {selected.length > 0 && (
            <SelectedTag>선택 {selected.length}개</SelectedTag>
          )}
          {currentUser?.grade !== "SELLER" && (
            <Button size={isMobile ? 'small' : 'middle'} type="primary" icon={<PlusOutlined />} onClick={onAdd} disabled={isRestricted}>
              슬롯 추가
            </Button>
          )}
          <Button size={isMobile ? 'small' : 'middle'} icon={<FileTextOutlined />} onClick={() => setGuideOpen(true)}>엑셀 가이드</Button>
          {currentUser?.grade !== "SELLER" && (
          <Upload
           beforeUpload={file => {
            onExcelParsedUpload(file);
            return false;
          }}
            showUploadList={false}
            disabled={isRestricted}
          >
            <Button icon={<UploadOutlined />} loading={excelUploading} disabled={isRestricted} size={isMobile ? 'small' : 'middle'}>
              엑셀 업로드
            </Button>
          </Upload>
          )}
          {currentUser?.grade !== "SELLER" && (
          <Upload
           beforeUpload={file => {
            onExcelBatchUpdateUpload(file);
            return false;
          }}
            showUploadList={false}
            disabled={isRestricted}
          >
            <Button icon={<FormOutlined />} loading={excelUploading} disabled={isRestricted} size={isMobile ? 'small' : 'middle'}>
              엑셀 수정
            </Button>
          </Upload>
          )}
          <Button size={isMobile ? 'small' : 'middle'} icon={<DownloadOutlined />} onClick={onExcelDownload} loading={excelDownloading}>
            엑셀 다운로드{selected.length > 0 && ` (${selected.length}개)`}
          </Button>
          {isAdpa99Username(currentUser?.username) && (
            <Tooltip title="현재 목록에 표시된 순위를 포함해 엑셀로 받습니다. adpa99 전용">
              <Button
                size={isMobile ? 'small' : 'middle'}
                icon={<SyncOutlined />}
                onClick={onExcelDownloadWithLatestRanks}
              >
                현재 순위 엑셀{selected.length > 0 && ` (${selected.length}개)`}
              </Button>
            </Tooltip>
          )}
          {currentUser?.grade === 'ADMIN' && (
            <Button
              size={isMobile ? 'small' : 'middle'}
              type="default"
              icon={<SearchOutlined />}
              onClick={() => {
                if (!hasSingle || !prefill) return;
                navigate('/keyword-optimization', { state: { keywordPrefill: prefill } });
              }}
              disabled={!hasSingle || !prefill}
            >
              키워드별 순위
            </Button>
          )}
        </RightCluster>
      </Row>

      <Row>
        <Space size={isMobile ? 'small' : 'middle'} wrap>
          <Button size={isMobile ? 'small' : 'middle'} icon={<ClockCircleOutlined />} onClick={onExtend} disabled={!hasSelection || !batchPermissions.canExtend}>연장</Button>
          <Button size={isMobile ? 'small' : 'middle'} icon={<EditOutlined />} onClick={onEdit} disabled={!hasSingle || !batchPermissions.canEdit}>수정</Button>
          <Button size={isMobile ? 'small' : 'middle'} icon={<FormOutlined />} onClick={onBatchEdit} disabled={!hasSelection || !batchPermissions.canBatchEdit}>일괄 수정</Button>
          <Button size={isMobile ? 'small' : 'middle'} danger icon={<RedoOutlined />} onClick={onRefund} disabled={!hasSelection || !batchPermissions.canRefund}>환불</Button>
          {currentUser?.grade !== "SELLER" && (
            <Button size={isMobile ? 'small' : 'middle'} danger icon={<DeleteOutlined />} onClick={onDelete} disabled={!hasSelection || !batchPermissions.canDelete}>삭제</Button>
          )}
        </Space>
        <Space
          size={isMobile ? 'small' : 'middle'}
          wrap
          style={{ width: isMobile ? '100%' : undefined, marginLeft: isMobile ? undefined : 'auto' }}
        >
          {localFindOpen && (
            <Space
              className="slot-local-find"
              size={isMobile ? 'small' : 'middle'}
              direction={isMobile ? 'vertical' : 'horizontal'}
              style={{ width: isMobile ? '100%' : 'auto' }}
            >
              <Input
                ref={localFindInputRef}
                placeholder="현재 페이지에서 찾기"
                value={localFindQuery}
                onChange={e => onLocalFindQueryChange(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Escape') {
                    e.preventDefault();
                    onCloseLocalFind();
                    return;
                  }

                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (e.shiftKey) onLocalFindPrev();
                    else onLocalFindNext();
                  }
                }}
                allowClear
                style={{ width: isMobile ? '100%' : 240 }}
              />
              <span style={{ minWidth: 64, textAlign: 'center' }}>{localFindStatusText}</span>
              <Space size="small">
                <Button
                  aria-label="이전 결과"
                  icon={<UpOutlined />}
                  onClick={onLocalFindPrev}
                  disabled={!hasLocalFindQuery || localFindMatchCount === 0}
                />
                <Button
                  aria-label="다음 결과"
                  icon={<DownOutlined />}
                  onClick={onLocalFindNext}
                  disabled={!hasLocalFindQuery || localFindMatchCount === 0}
                />
                <Button aria-label="닫기" icon={<CloseOutlined />} onClick={onCloseLocalFind} />
              </Space>
            </Space>
          )}
        </Space>
      </Row>

      <ExcelGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} />
    </Bar>
  );
};

export default ManagementBar;
